/**
 * Error message mapping utility
 * Maps backend error messages to i18n translation keys
 */

/**
 * Map of backend error messages to translation keys
 * When backend returns these exact messages, they can be translated
 */
const ERROR_MESSAGE_MAP = {
  "Username and password required":
    "auth.api_messages.username_password_required",
  "Invalid token response from Keycloak":
    "auth.api_messages.invalid_token_response",
  "Email verification required. Please check your email and verify your account before logging in.":
    "auth.api_messages.email_verification_required",
  "Invalid username or password": "auth.api_messages.invalid_username_password",
  "Refresh token required": "auth.api_messages.refresh_token_required",
  "Failed to refresh token": "auth.api_messages.failed_refresh_token",
  "Successfully logged out": "auth.api_messages.logged_out_success",
  "Logout completed": "auth.api_messages.logout_completed",
  "Failed to retrieve user information": "auth.api_messages.failed_user_info",
  "Email is required": "auth.api_messages.email_required",
  "User not found": "auth.api_messages.user_not_found",
  "Current password and new password are required":
    "auth.api_messages.current_new_password_required",
  "Password updated successfully": "auth.api_messages.password_updated_success",
  "Password updated successfully.":
    "auth.api_messages.password_updated_success",
  "Failed to update password": "auth.api_messages.failed_update_password",
  "Employee email is required": "auth.api_messages.employee_email_required",
  "Company lookup is not yet integrated; no match information available.":
    "auth.api_messages.company_lookup_not_integrated",
  "Email already registered": "auth.api_messages.email_already_registered",
  "Email already verified. Please sign in.":
    "auth.api_messages.email_already_verified",
  "Username already taken": "auth.api_messages.username_already_taken",
  "Invalid role for self-registration. Only 'student' or 'company_user' allowed":
    "auth.api_messages.invalid_role_self_registration",
  "Registration successful. Please verify your email before logging in.":
    "auth.api_messages.registration_success",
  "Failed to register user in Keycloak":
    "auth.api_messages.failed_register_user",
  "Permission denied. Only platform administrators can promote users.":
    "auth.api_messages.permission_denied_promote",
  "User has 'student' role. Student role cannot be changed to company_user or company_admin.":
    "auth.api_messages.student_role_cannot_change",
  "User does not have 'company_user' role. Cannot promote users without this role.":
    "auth.api_messages.user_not_company_user",
  "User already has 'company_admin' role. Cannot promote again.":
    "auth.api_messages.user_already_company_admin",
  "User successfully promoted to company_admin":
    "auth.api_messages.promoted_to_company_admin",
  "Failed to promote user in Keycloak": "auth.api_messages.failed_promote_user",
  "If an account exists for this email, a password reset link has been sent.":
    "auth.api_messages.password_reset_sent",
};

const RATE_LIMIT_PREFIX = "Rate limit exceeded";

/**
 * Get translation key for a backend error message
 *
 * @param {string} errorMessage - Error message from backend
 * @param {Function} t - i18n translation function
 * @param {string} fallback - Optional fallback message/translation key if no mapping found
 * @returns {string} Translated message or fallback
 *
 * @example
 * import { getLocalizedErrorMessage } from '@/utils/errorMessages';
 * import { useTranslation } from 'react-i18next';
 *
 * const { t } = useTranslation();
 * const localizedError = getLocalizedErrorMessage(
 *   error.message,
 *   t,
 *   t("auth.login.invalid_credentials") // fallback
 * );
 */
export function getLocalizedErrorMessage(errorMessage, t, fallback = "") {
  if (!errorMessage) return fallback;

  if (errorMessage.startsWith(RATE_LIMIT_PREFIX)) {
    return t("auth.api_messages.rate_limit_exceeded");
  }

  // Check if we have a translation key for this exact message
  const translationKey = ERROR_MESSAGE_MAP[errorMessage];

  if (translationKey) {
    return t(translationKey);
  }

  // If no exact match, return fallback if provided, otherwise original message
  return fallback || errorMessage;
}

/**
 * Map of request field names to their translation keys, used to render
 * a human-friendly field label inside localized validation messages.
 */
const VALIDATION_FIELD_KEYS = {
  password: "auth.validation.fields.password",
  confirm_password: "auth.validation.fields.confirm_password",
  email: "auth.validation.fields.email",
  username: "auth.validation.fields.username",
  first_name: "auth.validation.fields.first_name",
  last_name: "auth.validation.fields.last_name",
  designation: "auth.validation.fields.designation",
  dob: "auth.validation.fields.dob",
};

/**
 * Build a localized message from a FastAPI/Pydantic 422 validation error.
 *
 * The backend sends raw English text (e.g. "String should have at least 8
 * characters"). We instead translate from the structured payload fields
 * (type + loc + ctx) so the message respects the active language.
 *
 * @param {Error} error - Error thrown by apiRequest (has `payload`)
 * @param {Function} t - i18n translation function
 * @returns {string|null} Localized message, or null if not a parseable 422
 */
export function getValidationErrorMessage(error, t) {
  const detail = error?.payload?.detail;
  if (!Array.isArray(detail) || detail.length === 0) return null;

  const first = detail[0];
  if (!first || typeof first !== "object") return null;

  const loc = Array.isArray(first.loc) ? first.loc : [];
  const fieldName = loc.filter((part) => part !== "body").pop() || "";
  const fieldKey = VALIDATION_FIELD_KEYS[fieldName];
  const field = fieldKey ? t(fieldKey) : fieldName;
  const ctx = first.ctx || {};

  switch (first.type) {
    case "string_too_short":
      return t("auth.validation.string_too_short", {
        field,
        min: ctx.min_length,
      });
    case "string_too_long":
      return t("auth.validation.string_too_long", {
        field,
        max: ctx.max_length,
      });
    case "missing":
      return t("auth.validation.missing", { field });
    case "string_pattern_mismatch":
      return t("auth.validation.pattern", { field });
    case "value_error":
      if (fieldName === "email") return t("auth.validation.invalid_email");
      return t("auth.validation.invalid", { field });
    default:
      return t("auth.validation.invalid", { field });
  }
}

/**
 * Check if an error message has a translation
 *
 * @param {string} errorMessage - Error message to check
 * @returns {boolean} True if translation exists
 */
export function hasTranslation(errorMessage) {
  return errorMessage in ERROR_MESSAGE_MAP;
}

/**
 * Get the translation key for an error message without translating
 * Useful for logging or debugging
 *
 * @param {string} errorMessage - Error message from backend
 * @returns {string|null} Translation key or null if not found
 */
export function getTranslationKey(errorMessage) {
  return ERROR_MESSAGE_MAP[errorMessage] || null;
}
